import { useCallback, useMemo } from "react";

import type { InputMaybe } from "@/lib/gql/__generated__/graphql";
import { MealType } from "@/lib/gql/__generated__/graphql";

import type { ForArrayType } from "@/components/features/_model/types/auto-generator-for-view-model";

/**
 * 食事の並び（朝・昼・夕・素泊まり）
 * 付け外しの順に依らず同じ配列にそろえ、集合が同じなのに「変更あり」と判定されるのを防ぐ
 */
const MEAL_ORDER: MealType[] = [MealType.Breakfast, MealType.Lunch, MealType.Dinner, MealType.RoomOnly];

type Props = {
	includedMeals: ForArrayType<InputMaybe<MealType[]> | undefined> | undefined;
	onSetViewModel: (
		values: {
			key: "includedMeals";
			value: ForArrayType<MealType[]>;
		}[],
	) => void;
};

export const usePlanMealConditions = ({ includedMeals, onSetViewModel }: Props) => {
	const rawIncludedMeals = useMemo(() => includedMeals?.value?.map((meal) => meal.value) ?? [], [includedMeals]);

	const onToggleMeal = useCallback(
		(mealType: MealType) => {
			// 素泊まりと食事の併記は、ここでは制限せず、zod のバリデーション（includedMealsZodSchema）のエラーとして出す
			const nextMeals = rawIncludedMeals.includes(mealType)
				? rawIncludedMeals.filter((meal) => meal !== mealType)
				: [...rawIncludedMeals, mealType];

			onSetViewModel([
				{
					key: "includedMeals",
					value: {
						isEdited: true,
						hasError: false,
						value: MEAL_ORDER.filter((meal) => nextMeals.includes(meal)).map((meal) => {
							return {
								isEdited: true,
								value: meal,
							};
						}),
					},
				},
			]);
		},
		[rawIncludedMeals, onSetViewModel],
	);

	return {
		isEdited: includedMeals?.isEdited ?? false,
		hasError: includedMeals?.hasError ?? false,
		error: includedMeals?.error,
		rawIncludedMeals,
		onToggleMeal,
	};
};
